import React, { useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ZionForm, { useZionForm, FormAutosuggest, useWatch } from '@fs/zion-form'
import { InfoSheetPanel, Button, DataBlock, H3, Row, Separator, usePageWidth } from '@fs/zion-ui'
import { sexTypes, useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import { stepsMap } from '../../providers/FamilyReviewReducer'
import { useFamilyReviewStateContext, useFamilyReviewDispatchContext, STEPS } from '../../providers/FamilyReviewContext'
import HighlightButton from '../highlightButton/HighlightButton'
import { getGenderedRelTypeSuggestions, sortFunction } from '../../utils/utils'
import FormStandardizedSexField from '../../../../components/standardizedSexField/StandardizedSexField'

export default function CreateNewPersonFormPage2({
  createNewPersonFormPage1Overlay,
  createNewPersonFormPage2Overlay,
  stepOnePartTwoOverlay,
  handleSubmit,
  givenField,
  surnameField,
  primaryPersonName,
  editHighlightsOverlay,
  currentTask,
  headingColor = 'green',
  isPrimaryPerson = false,
  stepTwoOverlay,
}) {
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const zionSlsState = useSlsStateContext()
  const state = useFamilyReviewStateContext()
  const dispatch = useFamilyReviewDispatchContext()

  const methods = useZionForm({
    mode: 'all',
    defaultValues: {
      sexField: { key: '', primaryText: '' },
      relationshipField: { key: '', primaryText: '' },
    },
    resolver: (values) => {
      const errors = {}
      methods.clearErrors()
      const { relationshipField, sexField } = values
      if (!sexField?.key || !sexTypes.includes(sexField?.key)) {
        errors.sexField = { message: t('standardizedSexField.validation.warning', 'No Standard Selected') }
      }
      if (!isPrimaryPerson && !relationshipField)
        errors.relationshipField = {
          type: 'required',
          message: t('FormValidation.notEmpty', 'Field cannot be empty.'),
        }
      return { values, errors }
    },
  })

  const [sexField] = useWatch({
    name: ['sexField'],
    control: methods.control,
  })

  const relationshipSuggestions = useMemo(() => {
    return getGenderedRelTypeSuggestions(zionSlsState.types, sexField?.key) || []
  }, [zionSlsState.types, sexField])

  useEffect(() => {
    if (createNewPersonFormPage2Overlay.isOpen) {
      methods.reset({
        sexField: { key: '', primaryText: '' },
        relationshipField: { key: '', primaryText: '' },
      }) // Reset this whenever overlay opens
      methods.reset({
        sexField: '',
        relationshipField: null,
      }) // Reset this whenever overlay opens
    }
  }, [createNewPersonFormPage2Overlay.isOpen, state.addedSexId, methods])

  const handleHighlightButtonClicked = (id) => {
    if (id) dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: id })
    h.setDisplayHighlights({
      mainPartitionKey: currentTask.partitionKey,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
      setMainZoom: true,
      setSplitScreenZoom: Boolean(state.splitScreenPartitionKey),
      attachFilter: (hl) => hl.fieldId === id,
      mainZoomFilter: (hl) => hl.fieldId === id,
      splitScreenZoomFilter: (hl) => hl.fieldId === id,
      isSmallScreen,
    })
  }

  const createBackButtonClicked = () => {
    createNewPersonFormPage2Overlay.close()
  }

  const createSaveButtonClicked = () => {
    handleSubmit(methods.getValues(), state.addedSexId)
    if (isPrimaryPerson) {
      stepTwoOverlay.open()
      stepOnePartTwoOverlay.close()
      createNewPersonFormPage1Overlay.close()
      createNewPersonFormPage2Overlay.close()
      dispatch({ type: 'SET_CURRENT_STEP', value: STEPS.STEP_TWO })
    }
  }
  const newPersonName = [givenField, surnameField].filter(Boolean).join(' ')

  return (
    <InfoSheetPanel
      {...createNewPersonFormPage2Overlay}
      headingColor={headingColor}
      title={
        state.currentStep === stepsMap.STEP_ONE
          ? t('family-review.step1.heading', 'Identify Primary Person')
          : t('family-review.step3.heading', 'Family Relationships')
      }
      footer={
        <Row alignX="end">
          <Button
            onClick={createBackButtonClicked}
            data-testid="add-person-create-form-back-button"
            linkName="Add Person Go Back"
          >
            {t('navigation.back-button', 'Back')}
          </Button>
          <Button
            emphasis="high"
            linkName="Add Person Save"
            data-testid="add-person-create-form-save-button"
            type="submit"
            form="create-new-person-two"
          >
            {isPrimaryPerson ? t('navigation.update-button', 'Update') : t('navigation.save-button', 'Save')}
          </Button>
        </Row>
      }
    >
      {createNewPersonFormPage2Overlay.isOpen && (
        <>
          <H3 size="H5">
            {isPrimaryPerson
              ? t('family-review.addPerson.add-new-primary', 'Add New Primary Person')
              : t('family-review.addPerson.add-new', 'Add new Person')}
          </H3>
          <Separator size="sm" />
          <DataBlock
            heading={newPersonName}
            data={
              isPrimaryPerson
                ? t('family-review.addPerson.select-sex-only', 'Select the sex of {newPersonName}.', {
                    newPersonName,
                  })
                : t(
                    'family-review.addPerson.select-instructions',
                    'Select the sex of {newPersonName} and the relationship to {primaryPersonName}.',
                    {
                      newPersonName,
                      primaryPersonName,
                    }
                  )
            }
          />

          <Separator size="xs" />
          <ZionForm methods={methods} onSubmit={createSaveButtonClicked} id="create-new-person-two">
            <FormStandardizedSexField
              fieldName="sexField"
              key={state.addedSexId}
              addedSexCode={{
                id: state.addedSexId,
                displayLabel: t('edit-essential-info.sex-code', 'Sex'),
              }}
              onChange={() => {
                methods.setValue('relationshipField', { key: '', primaryText: '' })
              }}
            />
            <HighlightButton
              handleHighlightButtonClicked={handleHighlightButtonClicked}
              editHighlightsOverlay={editHighlightsOverlay}
              id={state.addedSexId}
              isAdding
            >
              {t('add-person.highlight-button', 'Add Highlight')}
            </HighlightButton>
            {isPrimaryPerson ? (
              ''
            ) : (
              <FormAutosuggest
                data-testid="addPersonRelationshipField"
                name="relationshipField"
                label={t('family-review.addPerson.relationship-to-person', 'Relationship To {primaryPersonName}', {
                  primaryPersonName,
                })}
                placeholder={t(
                  'family-review.addPerson.relationship-to-person',
                  'Relationship To {primaryPersonName}',
                  {
                    primaryPersonName,
                  }
                )}
                suggestions={relationshipSuggestions}
                sortFunction={sortFunction}
                highlightMatchingText
              />
            )}
          </ZionForm>
        </>
      )}
    </InfoSheetPanel>
  )
}
